import React, { useState, useEffect, useMemo } from 'react';
import useChargingSessionsByStation from '../hooks/useChargingSessionsByStation';
import { toast } from 'react-hot-toast';
import { exportToCSV } from '../utils/exportData';
import { formatINR } from "../utils/formatINR";

const ChargingSessionsDesktop = ({ stationId, range }) => {
  const { sessions, isLoading, error, message } = useChargingSessionsByStation(stationId, range);
  const [localSessions, setLocalSessions] = useState([]);

  // Clear stale data immediately when filters change
  useEffect(() => {
    setLocalSessions([]);
  }, [stationId, range?.fromDate, range?.toDate]);

  // Format timestamp to readable format
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid Date';

      const options = {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };

      return date.toLocaleDateString('en-IN', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  // Format just the time part
  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid Time';

      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Time';
    }
  };

  const handleExport = () => {
    exportToCSV(localSessions, `charging_sessions_${stationId}`);
  };

  useEffect(() => {
    if (message) toast.success(message);
  }, [message]);

  // Update table data from API; handle null/empty without clearing on disconnect
  useEffect(() => {
    if (Array.isArray(sessions)) {
      const cleaned = sessions.map(s => ({
        ...s,
        user_id: s.user_id?.Valid ? s.user_id.String : "",
      }));
      setLocalSessions(cleaned);
    }
    // If sessions is null/undefined (e.g., socket disconnected), preserve previous localSessions
  }, [sessions]);
  

  const totals = useMemo(() => {
    return localSessions.reduce((acc, s) => {
      const energy = parseFloat(s.energy_kwh) || 0;
      const total = parseFloat(s.total_cost) || 0;
      const gst = parseFloat(s.gst_amount) || 0;
      const finalCost = parseFloat(s.final_cost) || 0;
      return {
        energy_kwh: acc.energy_kwh + energy,
        total_cost: acc.total_cost + total,
        gst_amount: acc.gst_amount + gst,
        final_cost: acc.final_cost + finalCost,
      };
    }, { energy_kwh: 0, total_cost: 0, gst_amount: 0, final_cost: 0 });
  }, [localSessions]);

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-2 sm:px-2 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-semibold tracking-tight text-ink">Charging Sessions</h1>
            <button
              onClick={handleExport}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              Export Data
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-full mx-auto px-2 sm:px-0 lg:px-0 py-4">
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Charging Sessions for Station {stationId}
            </h2>
          </div>

          {/* Scrollable Table Container */}
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gun ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Energy (kWh)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SOC (Start → End)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration (min)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gst</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Final Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Your Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Time</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {localSessions.map((s, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.vehicle_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.gun_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.energy_kwh}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.start_soc} → {s.end_soc}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Math.round(s.duration_seconds / 60)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(s.total_cost)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(s.gst_amount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(s.final_cost)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(s.final_cost)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="text-xs">
                        <div className="font-medium">{formatDateTime(s.session_start).split(', ')[0]}</div>
                        <div className="text-gray-400">{formatTime(s.session_start)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="text-xs">
                        <div className="font-medium">{s.session_end ? formatDateTime(s.session_end).split(', ')[0] : 'Ongoing'}</div>
                        <div className="text-gray-400">{s.session_end ? formatTime(s.session_end) : ''}</div>
                      </div>
                    </td>
                  </tr>
                ))}

                {/* Totals Row */}
                {localSessions.length > 0 && (
                  <tr className="bg-gray-50 font-medium">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Total</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{totals.energy_kwh.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">-</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">-</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(totals.total_cost)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(totals.gst_amount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(totals.final_cost)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatINR(totals.final_cost)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                  </tr>
                )}

                {localSessions.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan="11" className="px-6 py-8 text-center text-sm text-gray-500">
                      No sessions for the selected range. Try adjusting the dates or refreshing.
                    </td>
                  </tr>
                )}

                {isLoading && (
                  <tr>
                    <td colSpan="11" className="px-6 py-4 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                )}

                {/* {error && (
                  <tr>
                    <td colSpan="11" className="px-6 py-4 text-center text-sm text-red-500">
                      {error}
                    </td>
                  </tr>
                )} */}
              </tbody>
            </table>
          </div>

        </div>
      </main>
    </div>
  );
};

export default ChargingSessionsDesktop;
